import { ref, computed, onUnmounted } from 'vue'
import { VideoClient, VideoClientConfig, VideoProviderType } from '../types/video-provider'
import { VideoClientFactory } from '../factories/VideoClientFactory'
import { VanillaWebRTCClient } from '../clients/VanillaWebRTCClient'
import type { OfferEvent, AnswerEvent, IceCandidateEvent } from '../types/websocket'

/**
 * Signaling Service Interface
 * Required for vanilla WebRTC (not needed for Zoom/Chime)
 */
export interface SignalingServiceInterface {
  on(event: string, callback: Function): void
  off(event: string, callback: Function): void
  sendOffer(sessionId: string, offer: RTCSessionDescriptionInit, from: string): void
  sendAnswer(sessionId: string, answer: RTCSessionDescriptionInit, from: string): void
  sendIceCandidate(sessionId: string, candidate: RTCIceCandidateInit, from: string): void
  joinRoom(roomId: string): void
}

/**
 * Session Credentials from API
 */
export interface SessionCredentials {
  token?: string
  sessionName?: string
  userName?: string
  role?: string
  [key: string]: any
}

/**
 * Use Video Client Composable
 * Provider-agnostic composable for video calls
 * 
 * Supports multiple providers:
 * - vanilla-webrtc: Uses WebRTC with WebSocket signaling
 * - zoom: Uses Zoom Video SDK with JWT tokens
 * - chime: Uses AWS Chime SDK (future)
 * 
 * @param sessionId - Session identifier
 * @param userId - User identifier
 * @param role - User role ('nurse' or 'patient')
 * @param providerType - Provider type (from API response or environment)
 * @param credentials - Authentication credentials (e.g., Zoom JWT token)
 * @param signalingService - Signaling service (required for vanilla WebRTC only)
 */
export function useVideoClient(
  sessionId: string,
  userId: string,
  _role: 'nurse' | 'patient',
  providerType: VideoProviderType,
  credentials?: SessionCredentials,
  signalingService?: SignalingServiceInterface
) {
  const localStream = ref<MediaStream | null>(null)
  const remoteStream = ref<MediaStream | null>(null)
  const isConnected = ref(false)
  const error = ref<string | null>(null)
  const videoClient = ref<VideoClient | null>(null)

  // For vanilla WebRTC only
  const peerConnection = ref<RTCPeerConnection | null>(null)

  // Closed-captioning state
  const captionsEnabled = ref(false)
  const currentCaption = ref<string>('')
  const captionHistory = ref<Array<{ text: string; timestamp: number; speakerId?: string }>>([])

  /**
   * Initialize video client
   */
  const initializeClient = async () => {
    try {
      console.log('[useVideoClient] Initializing client', { providerType })

      const { requireEnv } = await import('../utils/env')
      
      let apiUrl = ''
      let socketUrl = ''
      const stunServer = 'stun:stun.l.google.com:19302'
      
      // Detect build system by directly accessing env vars
      // Webpack replaces process.env.VUE_APP_* with string literals at build time
      // Vite provides import.meta.env.VITE_* at runtime
      
      // Try Webpack vars (direct access allows static replacement)
      let hasWebpackVars = false
      try {
        // @ts-ignore - webpack replaces process.env.X at build time
        if (process.env.VUE_APP_API_BASE_URL) {
          // @ts-ignore
          apiUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, 'VUE_APP_API_BASE_URL')
          // @ts-ignore
          socketUrl = requireEnv(process.env.VUE_APP_SOCKET_URL, 'VUE_APP_SOCKET_URL')
          hasWebpackVars = true
        }
      } catch (e) {
        // process not defined or env vars not set
      }
      
      // Try Vite vars if Webpack vars not found
      if (!hasWebpackVars) {
        if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
          const viteEnv = (import.meta as any).env
          if (viteEnv.VITE_API_BASE_URL) {
            apiUrl = requireEnv(viteEnv.VITE_API_BASE_URL, 'VITE_API_BASE_URL')
            socketUrl = requireEnv(viteEnv.VITE_SOCKET_URL, 'VITE_SOCKET_URL')
          } else {
            throw new Error('Missing required Vite environment variables: VITE_API_BASE_URL, VITE_SOCKET_URL')
          }
        } else {
          throw new Error('Unable to detect build system: No environment variables found')
        }
      }
      
      const appConfig = {
        apiUrl,
        socketUrl,
        stunServer,
      }

      console.log('[useVideoClient] Using resolved config', appConfig)
      
      const config: VideoClientConfig = {
        apiUrl: appConfig.apiUrl,
        socketUrl: appConfig.socketUrl,
        stunServers: [appConfig.stunServer].filter(Boolean),
      }

      // Create appropriate client based on provider (now async due to lazy loading)
      console.log('[useVideoClient] Creating video client...')
      videoClient.value = await VideoClientFactory.createClient(providerType, config)
      console.log('[useVideoClient] Video client created')
      
      console.log('[useVideoClient] Initializing video client...')
      await videoClient.value.initialize(config)
      console.log('[useVideoClient] Video client initialized')
    } catch (err) {
      console.error('[useVideoClient] Failed to initialize client:', err)
      throw err
    }

    // Set up event listeners
    videoClient.value.on('connected', () => {
      console.log('[useVideoClient] Connected')
      isConnected.value = true
    })

    videoClient.value.on('disconnected', () => {
      console.log('[useVideoClient] Disconnected')
      isConnected.value = false
    })

    videoClient.value.on('remote-stream-added', (stream: MediaStream) => {
      console.log('[useVideoClient] Remote stream added!', {
        streamId: stream.id,
        tracks: stream.getTracks().map(t => ({ kind: t.kind, id: t.id, enabled: t.enabled }))
      })
      remoteStream.value = stream
      console.log('[useVideoClient] Remote stream set in composable')
    })

    videoClient.value.on('error', (err: Error) => {
      console.error('[useVideoClient] Error:', err)
      error.value = err.message
    })

    // Handle media diagnostics event (from ZoomClient or other providers)
    console.log('[useVideoClient] Registering media_diagnostics event listener')
    videoClient.value.on('media_diagnostics', (diagnostics: any) => {
      console.log('[useVideoClient] media_diagnostics event RECEIVED from video client')
      console.log('[useVideoClient] Diagnostics data:', JSON.stringify(diagnostics, null, 2))
      
      // Forward to backend via signaling service (if available)
      if (signalingService) {
        // Check if it has emitToBackend (new method) or fall back to emit
        if (typeof (signalingService as any).emitToBackend === 'function') {
          console.log('[useVideoClient] Sending media diagnostics to backend via WebSocket (emitToBackend)')
          ;(signalingService as any).emitToBackend('media_diagnostics', diagnostics)
          console.log('[useVideoClient] media_diagnostics sent to backend successfully')
        } else if (typeof (signalingService as any).emit === 'function') {
          console.log('[useVideoClient] Sending media diagnostics to backend via WebSocket (emit)')
          ;(signalingService as any).emit('media_diagnostics', diagnostics)
          console.log('[useVideoClient] media_diagnostics sent to backend successfully')
        } else {
          console.warn('[useVideoClient] Signaling service does not have emit method')
        }
      } else {
        console.warn('[useVideoClient] Signaling service not available - cannot send diagnostics to backend')
      }
    })

    // Set up caption event listeners
    videoClient.value.on('caption-received', (captionData: any) => {
      console.log('[useVideoClient] Caption received:', captionData)
      currentCaption.value = captionData.text
      
      // Add to history if it's a final caption
      if (captionData.isFinal) {
        captionHistory.value.push({
          text: captionData.text,
          timestamp: captionData.timestamp,
          speakerId: captionData.speakerId,
        })
        
        // Keep only last 50 captions to prevent memory leak
        if (captionHistory.value.length > 50) {
          captionHistory.value.shift()
        }
      }
    })

    videoClient.value.on('caption-enabled', () => {
      console.log('[useVideoClient] Captions enabled')
      captionsEnabled.value = true
    })

    videoClient.value.on('caption-disabled', () => {
      console.log('[useVideoClient] Captions disabled')
      captionsEnabled.value = false
      currentCaption.value = ''
    })

    console.log('[useVideoClient] Client initialized', { provider: providerType })
  }

  /**
   * Merge Pinia/API credentials with an optional per-call override (e.g. join API
   * fetched in ActiveCallView when the route loads without prior confirmCall).
   */
  const resolveZoomJoinCredentials = (override?: SessionCredentials): SessionCredentials => {
    const merged: SessionCredentials = { ...credentials, ...override }
    const token = merged.token
    if (typeof token !== 'string' || token.trim().length === 0) {
      throw new Error(
        'Zoom session token is missing. Join the session via the API or complete the call flow first.'
      )
    }
    const name =
      typeof merged.userName === 'string' && merged.userName.trim().length > 0
        ? merged.userName.trim()
        : userId
    return { ...merged, token: token.trim(), userName: name }
  }

  /**
   * Start call (for initiator/nurse)
   * @param credentialsOverride - Optional credentials when store was empty at composable setup
   */
  const startCall = async (credentialsOverride?: SessionCredentials) => {
    try {
      error.value = null

      // Initialize client if not already done
      if (!videoClient.value) {
        await initializeClient()
      }

      if (providerType === 'zoom') {
        // For Zoom: Join session FIRST, then get media
        const joinCreds = resolveZoomJoinCredentials(credentialsOverride)
        await videoClient.value!.joinSession(sessionId, joinCreds)
        console.log('[useVideoClient] Joined Zoom session')
        
        // CRITICAL: Wait for SDK internal initialization to complete
        // The error "shouldUseWebRTC undefined" occurs during SDK's async
        // post-join setup. Give it time to stabilize before media operations.
        console.log('[useVideoClient] Waiting 2s for SDK stabilization...')
        await new Promise(resolve => setTimeout(resolve, 2000))
        console.log('[useVideoClient] SDK stabilization wait complete')
        
        // Get local media AFTER joining and waiting
        // NOTE: Local camera may fail on some devices/browsers due to WebRTC capability limitations.
        // This is non-fatal - the call can proceed with remote video only.
        try {
          localStream.value = await videoClient.value!.getLocalMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
            },
          })
          console.log('[useVideoClient] Local media obtained')
        } catch (mediaErr) {
          console.warn('[useVideoClient] Failed to get local media - proceeding without local camera:', mediaErr)
          console.warn('[useVideoClient] Call will work in receive-only mode')
          // Don't throw - allow call to continue without local camera
        }
      } else {
        // For vanilla WebRTC: Get media first, then use WebSocket signaling
        localStream.value = await videoClient.value!.getLocalMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        })
        
        await startVanillaWebRTCCall()
      }

    } catch (err) {
      console.error('[useVideoClient] Error starting call:', err)
      error.value = err instanceof Error ? err.message : 'Failed to start call'
      throw err
    }
  }

  /**
   * Answer call (for answerer/patient)
   * @param credentialsOverride - Optional credentials when store was empty at composable setup
   */
  const answerCall = async (credentialsOverride?: SessionCredentials) => {
    try {
      error.value = null

      // Initialize client if not already done
      if (!videoClient.value) {
        await initializeClient()
      }

      if (providerType === 'zoom') {
        // For Zoom: Just join with token
        const joinCreds = resolveZoomJoinCredentials(credentialsOverride)
        await videoClient.value!.joinSession(sessionId, joinCreds)
        
        // Get local media for Zoom
        // NOTE: Local camera may fail on some devices/browsers due to WebRTC capability limitations.
        // This is non-fatal - the call can proceed with remote video only.
        try {
          localStream.value = await videoClient.value!.getLocalMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
            },
          })
          console.log('[useVideoClient] Local media obtained')
        } catch (mediaErr) {
          console.warn('[useVideoClient] Failed to get local media - proceeding without local camera:', mediaErr)
          console.warn('[useVideoClient] Call will work in receive-only mode')
        }

        console.log('[useVideoClient] Joined Zoom session')
      } else {
        // For vanilla WebRTC: Use WebSocket signaling
        await answerVanillaWebRTCCall()
      }

    } catch (err) {
      console.error('[useVideoClient] Error answering call:', err)
      error.value = err instanceof Error ? err.message : 'Failed to answer call'
      throw err
    }
  }

  /**
   * Vanilla WebRTC: Start call with signaling
   */
  const startVanillaWebRTCCall = async () => {
    if (!signalingService) {
      throw new Error('Signaling service required for vanilla WebRTC')
    }

    const client = videoClient.value as VanillaWebRTCClient
    
    // Initialize peer connection
    peerConnection.value = await client.initializePeerConnection()

    // Set up ICE candidate handling
    peerConnection.value.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[useVideoClient] Sending ICE candidate')
        signalingService.sendIceCandidate(sessionId, event.candidate.toJSON(), userId)
      }
    }

    // Add local tracks
    console.log('[useVideoClient] Adding local tracks to peer connection')
    localStream.value!.getTracks().forEach(track => {
      console.log('[useVideoClient] Adding track:', track.kind, track.id)
      peerConnection.value!.addTrack(track, localStream.value!)
    })
    console.log('[useVideoClient] All tracks added')

    // Create and send offer
    const offer = await peerConnection.value.createOffer()
    await peerConnection.value.setLocalDescription(offer)
    console.log('[useVideoClient] Sending offer')
    signalingService.sendOffer(sessionId, offer, userId)

    // Listen for answer
    const handleAnswer = async (event: AnswerEvent) => {
      if (event.sessionId === sessionId) {
        console.log('[useVideoClient] Received answer')
        await peerConnection.value!.setRemoteDescription(event.answer)
        signalingService.off('answer', handleAnswer)
      }
    }
    signalingService.on('answer', handleAnswer)

    // Listen for ICE candidates
    const handleIceCandidate = async (event: IceCandidateEvent) => {
      if (event.sessionId === sessionId && event.from !== userId) {
        console.log('[useVideoClient] Received ICE candidate')
        await peerConnection.value!.addIceCandidate(event.candidate)
      }
    }
    signalingService.on('ice-candidate', handleIceCandidate)
  }

  /**
   * Vanilla WebRTC: Answer call with signaling
   */
  const answerVanillaWebRTCCall = async () => {
    if (!signalingService) {
      throw new Error('Signaling service required for vanilla WebRTC')
    }

    const client = videoClient.value as VanillaWebRTCClient
    
    // Initialize peer connection
    peerConnection.value = await client.initializePeerConnection()

    // Track if media is ready
    let mediaReady = false

    // Set up offer handler
    const handleOffer = async (event: OfferEvent) => {
      if (event.sessionId === sessionId) {
        console.log('[useVideoClient] Received offer, waiting for media...')
        
        // Wait for media
        while (!mediaReady) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
        
        console.log('[useVideoClient] Media ready, setting remote description')
        await peerConnection.value!.setRemoteDescription(event.offer)

        // Create and send answer
        const answer = await peerConnection.value!.createAnswer()
        await peerConnection.value!.setLocalDescription(answer)
        console.log('[useVideoClient] Sending answer')
        signalingService.sendAnswer(sessionId, answer, userId)
        
        signalingService.off('offer', handleOffer)
      }
    }
    signalingService.on('offer', handleOffer)

    // Set up ICE candidate handling
    peerConnection.value.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[useVideoClient] Sending ICE candidate')
        signalingService.sendIceCandidate(sessionId, event.candidate.toJSON(), userId)
      }
    }

    // Listen for ICE candidates
    const handleIceCandidate = async (event: IceCandidateEvent) => {
      if (event.sessionId === sessionId && event.from !== userId) {
        console.log('[useVideoClient] Received ICE candidate')
        await peerConnection.value!.addIceCandidate(event.candidate)
      }
    }
    signalingService.on('ice-candidate', handleIceCandidate)

    // Get local media
    localStream.value = await videoClient.value!.getLocalMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
    })

    // Add local tracks
    localStream.value.getTracks().forEach(track => {
      console.log('[useVideoClient] Adding track:', track.kind)
      peerConnection.value!.addTrack(track, localStream.value!)
    })
    
    mediaReady = true
    console.log('[useVideoClient] Patient ready - tracks added')
  }

  /**
   * End call and clean up
   */
  const endCall = async () => {
    console.log('[useVideoClient] Ending call')

    if (providerType === 'zoom') {
      // Zoom SDK manages its own tracks — stop video/audio via SDK before leaving
      if (videoClient.value) {
        try { await videoClient.value.stopVideo() } catch { /* already stopped */ }
        try { await videoClient.value.muteAudio() } catch { /* already muted */ }
        await videoClient.value.leaveSession()
      }
    } else {
      // Vanilla WebRTC — stop raw MediaStream tracks
      if (localStream.value) {
        localStream.value.getTracks().forEach(track => {
          console.log(`[useVideoClient] Stopping track: ${track.kind} (${track.id})`)
          track.stop()
        })
      }
      if (remoteStream.value) {
        remoteStream.value.getTracks().forEach(track => track.stop())
      }
      if (videoClient.value) {
        await videoClient.value.leaveSession()
      }
      if (peerConnection.value) {
        peerConnection.value.close()
        peerConnection.value = null
      }
    }

    localStream.value = null
    remoteStream.value = null
    isConnected.value = false
    isAudioMuted.value = false
    isVideoStopped.value = false

    console.log('[useVideoClient] Call ended')
  }

  // Provider-level mute/video state for Zoom (Zoom streams have no real MediaStream
  // tracks, so track.enabled cannot be used as a state indicator).
  const isAudioMuted = ref(false)
  const isVideoStopped = ref(false)

  /**
   * Toggle audio mute/unmute
   */
  const toggleAudio = async () => {
    if (!videoClient.value) return

    if (providerType === 'zoom') {
      try {
        if (isAudioMuted.value) {
          await videoClient.value.unmuteAudio()
          isAudioMuted.value = false
          console.log('[useVideoClient] Zoom audio unmuted')
        } else {
          await videoClient.value.muteAudio()
          isAudioMuted.value = true
          console.log('[useVideoClient] Zoom audio muted')
        }
      } catch (err) {
        console.error('[useVideoClient] Failed to toggle audio:', err)
      }
    } else {
      // Vanilla WebRTC — disable/enable the actual audio track
      const audioTrack = localStream.value?.getAudioTracks()[0]
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled
      }
    }
  }

  /**
   * Toggle video on/off
   */
  const toggleVideo = async () => {
    if (!videoClient.value) return

    if (providerType === 'zoom') {
      try {
        if (isVideoStopped.value) {
          await videoClient.value.startVideo()
          isVideoStopped.value = false
          console.log('[useVideoClient] Zoom video started')
        } else {
          await videoClient.value.stopVideo()
          isVideoStopped.value = true
          console.log('[useVideoClient] Zoom video stopped')
        }
      } catch (err) {
        console.error('[useVideoClient] Failed to toggle video:', err)
      }
    } else {
      // Vanilla WebRTC — disable/enable the actual video track
      const videoTrack = localStream.value?.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled
      }
    }
  }

  // ==============================
  // Closed-Captioning Methods
  // ==============================

  /**
   * Check if captions are supported by the current provider
   */
  const supportsCaptions = computed(() => {
    return videoClient.value?.supportsClosedCaptions?.() ?? false
  })

  /**
   * Get available caption languages
   */
  const availableCaptionLanguages = computed(() => {
    return videoClient.value?.getAvailableCaptionLanguages?.() ?? []
  })

  /**
   * Enable closed captions
   * @param language - Language code (e.g., 'en-US', 'es-ES')
   */
  const enableCaptions = async (language: string = 'en-US') => {
    if (!videoClient.value) {
      throw new Error('Video client not initialized')
    }

    if (!videoClient.value.enableClosedCaptions) {
      throw new Error('Closed captions not supported by this provider')
    }

    try {
      await videoClient.value.enableClosedCaptions(language)
      captionsEnabled.value = true
      console.log(`[useVideoClient] Captions enabled: ${language}`)
    } catch (err) {
      console.error('[useVideoClient] Failed to enable captions:', err)
      throw err
    }
  }

  /**
   * Disable closed captions
   */
  const disableCaptions = async () => {
    if (!videoClient.value) {
      throw new Error('Video client not initialized')
    }

    if (!videoClient.value.disableClosedCaptions) {
      throw new Error('Closed captions not supported by this provider')
    }

    try {
      await videoClient.value.disableClosedCaptions()
      captionsEnabled.value = false
      currentCaption.value = ''
      console.log('[useVideoClient] Captions disabled')
    } catch (err) {
      console.error('[useVideoClient] Failed to disable captions:', err)
      throw err
    }
  }

  /**
   * Toggle captions on/off
   * @param language - Language code (default: 'en-US')
   */
  const toggleCaptions = async (language: string = 'en-US') => {
    if (captionsEnabled.value) {
      await disableCaptions()
    } else {
      await enableCaptions(language)
    }
  }

  /**
   * Cleanup on unmount
   */
  onUnmounted(async () => {
    await endCall()
    if (videoClient.value) {
      await videoClient.value.dispose()
    }
  })

  return {
    localStream,
    remoteStream,
    isConnected,
    error,
    startCall,
    answerCall,
    endCall,
    toggleAudio,
    toggleVideo,
    // Caption controls
    captionsEnabled,
    currentCaption,
    captionHistory,
    supportsCaptions,
    availableCaptionLanguages,
    enableCaptions,
    disableCaptions,
    toggleCaptions,
  }
}
